/**
 * SchemaForm — renders JSON Schema as actual form fields.
 *
 * Supports: string inputs, number inputs, boolean toggles, enum dropdowns,
 * nested objects (collapsible sections), optional fields, arrays of primitives.
 * Falls back to a JSON textarea for anything it can't render.
 */

import { useState } from 'react';

interface JsonSchema {
  type?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  enum?: string[];
  description?: string;
  title?: string;
  default?: unknown;
  items?: JsonSchema;
  oneOf?: JsonSchema[];
  anyOf?: JsonSchema[];
  allOf?: JsonSchema[];
  $ref?: string;
  additionalProperties?: JsonSchema | boolean;
  // schemars nesting
  definitions?: Record<string, JsonSchema>;
}

interface SchemaFormProps {
  schema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  /** Path prefix for field labels (used by nested objects) */
  path?: string;
}

export default function SchemaForm({ schema, value, onChange, path = '' }: SchemaFormProps) {
  const rootSchema = schema; // keep reference for $ref resolution
  const properties = resolveProperties(schema, rootSchema);
  const required = schema.required ?? [];

  if (!properties || Object.keys(properties).length === 0) {
    // Unknown schema — fallback to JSON textarea
    return <JsonFallback value={value} onChange={onChange} />;
  }

  const set = (key: string, v: unknown) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(properties).map(([key, prop]) => {
        const fieldPath = path ? `${path}.${key}` : key;
        const isRequired = required.includes(key);
        const resolved = resolveRef(prop, rootSchema);
        return (
          <FieldRenderer
            key={key}
            name={key}
            path={fieldPath}
            schema={resolved}
            rootSchema={rootSchema}
            value={value[key]}
            required={isRequired}
            onChange={v => set(key, v)}
          />
        );
      })}
    </div>
  );
}

interface FieldRendererProps {
  name: string;
  path: string;
  schema: JsonSchema;
  rootSchema: JsonSchema;
  value: unknown;
  required: boolean;
  onChange: (v: unknown) => void;
}

function FieldRenderer({
  name,
  path,
  schema,
  rootSchema,
  value,
  required,
  onChange,
}: FieldRendererProps) {
  const label = schema.title ?? humanize(name);
  const hint = schema.description;
  const type = inferType(schema);

  // Nullable wrapper (anyOf with null)
  const nullable = schema.anyOf?.some(s => s.type === 'null');
  const innerSchema = nullable
    ? schema.anyOf?.find(s => s.type !== 'null') ?? schema
    : schema;
  const innerType = nullable ? inferType(resolveRef(innerSchema, rootSchema)) : type;

  // Boolean → toggle switch
  if (innerType === 'boolean') {
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <label className="relative inline-flex items-center cursor-pointer gap-2">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!value}
            onChange={e => onChange(e.target.checked)}
          />
          <div className="w-9 h-5 bg-bg-3 border border-line-strong rounded-full peer-checked:bg-brand peer-focus-visible:ring-2 ring-brand/30 transition-colors after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-fg after:rounded-full after:h-3.5 after:w-3.5 after:transition-transform peer-checked:after:translate-x-4" />
          <span className="text-sm text-fg-2">{value ? 'Yes' : 'No'}</span>
        </label>
      </FieldWrapper>
    );
  }

  // Enum → dropdown
  const enumValues = innerSchema.enum ?? resolveRef(innerSchema, rootSchema).enum;
  if (enumValues) {
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <select
          className="input"
          value={(value as string) ?? ''}
          onChange={e => onChange(e.target.value || undefined)}
        >
          {!required && <option value="">— none —</option>}
          {enumValues.map((v: string) => (
            <option key={v} value={v}>
              {humanize(v)}
            </option>
          ))}
        </select>
      </FieldWrapper>
    );
  }

  // Integer / Number → number input
  if (innerType === 'integer' || innerType === 'number') {
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <input
          type="number"
          className="input"
          value={value != null ? String(value) : ''}
          placeholder={nullable ? 'null (optional)' : '0'}
          step={innerType === 'integer' ? 1 : 'any'}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '' && !required) {
              onChange(undefined);
            } else {
              onChange(innerType === 'integer' ? parseInt(raw, 10) : parseFloat(raw));
            }
          }}
        />
      </FieldWrapper>
    );
  }

  // Map (object with additionalProperties, no fixed properties) → key-value editor
  const resolvedInner = resolveRef(innerSchema, rootSchema);
  const hasAdditionalProps = resolvedInner.additionalProperties && typeof resolvedInner.additionalProperties === 'object';
  const hasFixedProps = resolvedInner.properties && Object.keys(resolvedInner.properties).length > 0;
  if (innerType === 'object' && hasAdditionalProps && !hasFixedProps) {
    const valueSchema = resolveRef(resolvedInner.additionalProperties as JsonSchema, rootSchema);
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <MapField
          fieldName={name}
          value={(value as Record<string, unknown>) ?? {}}
          valueSchema={valueSchema}
          rootSchema={rootSchema}
          onChange={v => onChange(v)}
        />
      </FieldWrapper>
    );
  }

  // Nested object → collapsible section
  const objProps = resolveProperties(resolvedInner, rootSchema);
  if (innerType === 'object' && objProps && Object.keys(objProps).length > 0) {
    return (
      <CollapsibleSection label={label} hint={hint} path={path} nullable={nullable} hasValue={value != null} onClear={() => onChange(undefined)}>
        <SchemaForm
          schema={resolveRef(innerSchema, rootSchema)}
          value={(value as Record<string, unknown>) ?? {}}
          onChange={v => onChange(v)}
          path={path}
        />
      </CollapsibleSection>
    );
  }

  // Array of primitives → tag-like input (with optional lookup)
  if (innerType === 'array') {
    const arrayLookup = FIELD_LOOKUP_MAP[name];
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <ArrayField value={(value as string[]) ?? []} onChange={onChange} lookup={arrayLookup} />
      </FieldWrapper>
    );
  }

  // String with lookup → dropdown
  const stringLookup = FIELD_LOOKUP_MAP[name];
  if (innerType === 'string' && stringLookup) {
    return (
      <FieldWrapper label={label} hint={hint} required={required} path={path}>
        <LookupStringField
          value={(value as string) ?? ''}
          onChange={v => onChange(v || (required ? '' : undefined))}
          lookup={stringLookup}
          nullable={nullable}
        />
      </FieldWrapper>
    );
  }

  // String → text input (default)
  return (
    <FieldWrapper label={label} hint={hint} required={required} path={path}>
      <input
        type="text"
        className="input"
        value={(value as string) ?? ''}
        placeholder={nullable ? '(optional)' : ''}
        onChange={e => onChange(e.target.value || (required ? '' : undefined))}
      />
    </FieldWrapper>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function FieldWrapper({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required: boolean;
  path?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-1.5">
        {label}
        {required && <span className="text-bad text-[10px]">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-fg-muted mt-1">{hint}</div>}
    </div>
  );
}

function CollapsibleSection({
  label,
  nullable,
  hasValue,
  onClear,
  children,
}: {
  label: string;
  hint?: string;
  path?: string;
  nullable?: boolean;
  hasValue: boolean;
  onClear: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(hasValue);
  return (
    <div className="border border-line rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 bg-bg-1 text-sm font-medium text-fg-2 hover:bg-bg-3 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2">
          <span className="text-[10px] text-fg-muted">{open ? '▼' : '▶'}</span>
          {label}
          {!hasValue && nullable && (
            <span className="badge badge-muted text-[10px]">null</span>
          )}
        </span>
        {nullable && hasValue && (
          <button
            type="button"
            className="text-[11px] text-bad hover:underline"
            onClick={e => {
              e.stopPropagation();
              onClear();
              setOpen(false);
            }}
          >
            Clear
          </button>
        )}
      </button>
      {open && <div className="p-3 bg-bg-2 border-t border-line">{children}</div>}
    </div>
  );
}

function LookupStringField({
  value,
  onChange,
  lookup,
  nullable,
}: {
  value: string;
  onChange: (v: string | undefined) => void;
  lookup: LookupRef;
  nullable?: boolean;
}) {
  const [options, setOptions] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);

  useState(() => {
    const gameId = localStorage.getItem('forge.gameId') || '';
    if (!gameId) return;
    fetch(`/api/v1/games/${gameId}/entities?package=${lookup.package}&entity_type=${lookup.entity_type}&status=published`)
      .then(r => r.json())
      .then((rows: { entity_key: string }[]) => {
        setOptions(rows.map(r => r.entity_key));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  });

  if (loaded && options.length > 0) {
    return (
      <div>
        <select
          className="input"
          value={value}
          onChange={e => onChange(e.target.value || undefined)}
        >
          {(!value || nullable) && <option value="">— select {lookup.label} —</option>}
          {options.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <div className="text-[10px] text-fg-muted mt-0.5">
          Lookup: {lookup.package}/{lookup.entity_type}
        </div>
      </div>
    );
  }

  return (
    <div>
      <input
        type="text"
        className="input"
        value={value}
        placeholder={`${lookup.label} key (publish ${lookup.entity_type} entities first)`}
        onChange={e => onChange(e.target.value || undefined)}
      />
      {loaded && options.length === 0 && (
        <div className="text-[10px] text-fg-muted mt-0.5">
          No published {lookup.label}s found. Create and publish {lookup.entity_type} entities to see a dropdown.
        </div>
      )}
    </div>
  );
}

function ArrayField({
  value,
  onChange,
  lookup,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  lookup?: LookupRef;
}) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<string[]>([]);

  useState(() => {
    if (!lookup) return;
    const gameId = localStorage.getItem('forge.gameId') || '';
    if (!gameId) return;
    fetch(`/api/v1/games/${gameId}/entities?package=${lookup.package}&entity_type=${lookup.entity_type}&status=published`)
      .then(r => r.json())
      .then((rows: { entity_key: string }[]) => {
        setOptions(rows.map(r => r.entity_key));
      })
      .catch(() => {});
  });

  const add = () => {
    if (input.trim() && !value.includes(input.trim())) {
      onChange([...value, input.trim()]);
      setInput('');
    }
  };
  const remove = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i));
  };

  const available = options.filter(o => !value.includes(o));

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((v, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-bg-3 border border-line rounded text-xs text-fg-2"
          >
            {v}
            <button
              type="button"
              className="text-fg-muted hover:text-bad text-[10px] leading-none"
              onClick={() => remove(i)}
            >
              ×
            </button>
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-fg-muted italic">empty</span>
        )}
      </div>
      <div className="flex gap-2">
        {lookup && available.length > 0 ? (
          <select
            className="input flex-1"
            value={input}
            onChange={e => setInput(e.target.value)}
          >
            <option value="">— select {lookup.label} —</option>
            {available.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        ) : (
          <input
            className="input flex-1"
            placeholder={lookup ? `${lookup.label} key…` : 'add value…'}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          />
        )}
        <button type="button" className="btn" onClick={add}>
          Add
        </button>
      </div>
      {lookup && available.length === 0 && options.length === 0 && (
        <div className="text-[10px] text-fg-muted mt-1">
          Publish {lookup.entity_type} entities to see a dropdown.
        </div>
      )}
    </div>
  );
}

/**
 * MapField — edits HashMap<String, T> as a list of key-value rows.
 * The value side renders according to the `valueSchema` (Quantity → two number fields,
 * plain number → single input, nested object → inline fields).
 */
// ── Lookup Maps ────────────────────────────────────────────────────────────────

interface LookupRef { package: string; entity_type: string; label: string }

/** Map from MAP field name to the (package, entity_type) whose keys should be suggested. */
const KEY_LOOKUP_MAP: Record<string, LookupRef> = {
  contents: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  resource_inputs: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  amounts: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  resources: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  cost: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  item_inputs: { package: 'inventory', entity_type: 'item_definition', label: 'item' },
  xp: { package: 'progression', entity_type: 'progression_track', label: 'track' },
};

/** Map from STRING/ARRAY field name to the (package, entity_type) for lookup dropdowns. */
const FIELD_LOOKUP_MAP: Record<string, LookupRef> = {
  // Resource refs
  resource_id: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  resource_ref: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  ref_key: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  currency: { package: 'resource', entity_type: 'resource_type', label: 'resource' },
  // Item refs
  item_id: { package: 'inventory', entity_type: 'item_definition', label: 'item' },
  item_ref: { package: 'inventory', entity_type: 'item_definition', label: 'item' },
  // Loot refs
  loot_table: { package: 'loot', entity_type: 'loot_table', label: 'loot table' },
  // Progression refs
  skill_track: { package: 'progression', entity_type: 'progression_track', label: 'track' },
  // Crafting refs
  station: { package: 'crafting', entity_type: 'station', label: 'station' },
  // Combat refs
  monster: { package: 'combat', entity_type: 'monster', label: 'monster' },
  // Quest refs
  quest_id: { package: 'quest', entity_type: 'quest', label: 'quest' },
  // Zone refs
  target_zone: { package: 'world', entity_type: 'zone', label: 'zone' },
  respawn_zone: { package: 'world', entity_type: 'zone', label: 'zone' },
  // Array fields (abilities, completed_quests, unlocks_quests)
  abilities: { package: 'combat', entity_type: 'ability', label: 'ability' },
  completed_quests: { package: 'quest', entity_type: 'quest', label: 'quest' },
  unlocks_quests: { package: 'quest', entity_type: 'quest', label: 'quest' },
  event_quests: { package: 'quest', entity_type: 'quest', label: 'quest' },
  store_id: { package: 'shop', entity_type: 'store', label: 'store' },
  season_id: { package: 'liveops', entity_type: 'season', label: 'season' },
};

function MapField({
  fieldName,
  value,
  valueSchema,
  rootSchema,
  onChange,
}: {
  fieldName: string;
  value: Record<string, unknown>;
  valueSchema: JsonSchema;
  rootSchema: JsonSchema;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const [newKey, setNewKey] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const entries = Object.entries(value);
  const lookup = KEY_LOOKUP_MAP[fieldName];

  // Fetch suggestions for key lookup if applicable.
  useState(() => {
    if (!lookup) return;
    // Try to fetch existing entity keys from the schema list endpoint.
    // This is a best-effort fetch — if it fails we just don't show suggestions.
    const gameId = localStorage.getItem('forge.gameId') || '';
    if (!gameId) return;
    fetch(`/api/v1/games/${gameId}/entities?package=${lookup.package}&entity_type=${lookup.entity_type}&status=published`)
      .then(r => r.json())
      .then((rows: { entity_key: string }[]) => {
        setSuggestions(rows.map(r => r.entity_key).filter(k => !(k in value)));
      })
      .catch(() => {});
  });

  const addEntry = () => {
    if (!newKey.trim() || newKey in value) return;
    const defaultVal = buildDefaultValue(valueSchema, rootSchema);
    onChange({ ...value, [newKey.trim()]: defaultVal });
    setNewKey('');
  };

  const removeEntry = (key: string) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const updateEntry = (key: string, val: unknown) => {
    onChange({ ...value, [key]: val });
  };

  // Detect if the value schema is a simple type (number/integer/string) or an object.
  const resolvedValueSchema = resolveRef(valueSchema, rootSchema);
  const valueType = inferType(resolvedValueSchema);
  const valueProps = resolveProperties(resolvedValueSchema, rootSchema);
  const isSimpleValue = valueType === 'integer' || valueType === 'number' || valueType === 'string';
  const isObjectValue = valueType === 'object' && valueProps && Object.keys(valueProps).length > 0;

  return (
    <div className="flex flex-col gap-2">
      {entries.length === 0 && (
        <div className="text-xs text-fg-muted italic">No entries. Add one below.</div>
      )}
      {entries.map(([key, val]) => (
        <div key={key} className="flex items-start gap-2 bg-bg-1 border border-line rounded-md p-2">
          <div className="shrink-0 w-32">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-fg-dim mb-1">Key</div>
            <div className="font-mono text-sm text-fg break-all">{key}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-fg-dim mb-1">Value</div>
            {isSimpleValue ? (
              <input
                type={valueType === 'string' ? 'text' : 'number'}
                className="input"
                value={val != null ? String(val) : ''}
                step={valueType === 'integer' ? 1 : 'any'}
                onChange={e => {
                  const raw = e.target.value;
                  if (valueType === 'integer') updateEntry(key, parseInt(raw, 10) || 0);
                  else if (valueType === 'number') updateEntry(key, parseFloat(raw) || 0);
                  else updateEntry(key, raw);
                }}
              />
            ) : isObjectValue ? (
              <div className="border border-line rounded p-2 bg-bg-2">
                <InlineObjectEditor
                  schema={resolvedValueSchema}
                  rootSchema={rootSchema}
                  value={(val as Record<string, unknown>) ?? {}}
                  onChange={v => updateEntry(key, v)}
                />
              </div>
            ) : (
              <input
                type="text"
                className="input font-mono text-xs"
                value={typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                onChange={e => {
                  try { updateEntry(key, JSON.parse(e.target.value)); } catch { updateEntry(key, e.target.value); }
                }}
              />
            )}
          </div>
          <button
            type="button"
            className="shrink-0 text-bad hover:text-red-400 text-sm mt-5"
            onClick={() => removeEntry(key)}
          >
            ×
          </button>
        </div>
      ))}
      <div className="flex gap-2 mt-1">
        {suggestions.length > 0 ? (
          <select
            className="input"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
          >
            <option value="">— select {lookup?.label ?? 'key'} —</option>
            {suggestions
              .filter(s => !(s in value))
              .map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <input
            className="input"
            placeholder={lookup ? `${lookup.label} key…` : 'new key…'}
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEntry())}
            list={lookup ? `${fieldName}-suggestions` : undefined}
          />
        )}
        <button type="button" className="btn" onClick={addEntry} disabled={!newKey.trim()}>
          + Add
        </button>
      </div>
      {lookup && suggestions.length === 0 && (
        <div className="text-[10.5px] text-fg-muted mt-1">
          Tip: publish {lookup.label} entities first for a dropdown of available keys.
        </div>
      )}
    </div>
  );
}

/**
 * Inline editor for a small object (e.g. Quantity with {value, decimal_places}).
 * Renders each property as a labeled input in a horizontal row.
 */
function InlineObjectEditor({
  schema,
  rootSchema,
  value,
  onChange,
}: {
  schema: JsonSchema;
  rootSchema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const props = resolveProperties(schema, rootSchema) ?? {};
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(props).map(([key, propSchema]) => {
        const resolved = resolveRef(propSchema, rootSchema);
        const pType = inferType(resolved);
        return (
          <div key={key} className="flex-1 min-w-[80px]">
            <div className="text-[10px] uppercase tracking-wider font-medium text-fg-muted mb-0.5">{humanize(key)}</div>
            {pType === 'integer' || pType === 'number' ? (
              <input
                type="number"
                className="input text-sm"
                step={pType === 'integer' ? 1 : 'any'}
                value={value[key] != null ? String(value[key]) : ''}
                onChange={e => {
                  const raw = e.target.value;
                  onChange({
                    ...value,
                    [key]: pType === 'integer' ? parseInt(raw, 10) || 0 : parseFloat(raw) || 0,
                  });
                }}
              />
            ) : pType === 'boolean' ? (
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!value[key]}
                  onChange={e => onChange({ ...value, [key]: e.target.checked })}
                  className="accent-brand"
                />
                <span className="text-xs text-fg-2">{value[key] ? 'Yes' : 'No'}</span>
              </label>
            ) : (
              <input
                type="text"
                className="input text-sm"
                value={(value[key] as string) ?? ''}
                onChange={e => onChange({ ...value, [key]: e.target.value })}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Build a default value for a schema type (used when adding new map entries). */
function buildDefaultValue(schema: JsonSchema, root: JsonSchema): unknown {
  const resolved = resolveRef(schema, root);
  const t = inferType(resolved);
  if (t === 'integer' || t === 'number') return 0;
  if (t === 'boolean') return false;
  if (t === 'string') return '';
  if (t === 'object') {
    const props = resolveProperties(resolved, root);
    if (props) {
      const obj: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(props)) {
        obj[k] = buildDefaultValue(v, root);
      }
      return obj;
    }
    return {};
  }
  if (t === 'array') return [];
  return null;
}

function JsonFallback({
  value,
  onChange,
}: {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);

  const apply = () => {
    try {
      onChange(JSON.parse(text));
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div>
      <div className="text-xs text-fg-muted mb-2 italic">
        No schema available — editing as raw JSON.
      </div>
      <textarea
        className="input font-mono"
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={apply}
        style={{ minHeight: 200 }}
      />
      {err && <div className="text-xs text-bad mt-1">{err}</div>}
    </div>
  );
}

function humanize(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
}

function inferType(schema: JsonSchema): string {
  if (schema.type) return schema.type;
  if (schema.enum) return 'string';
  if (schema.properties) return 'object';
  if (schema.items) return 'array';
  if (schema.anyOf) return 'anyOf';
  if (schema.oneOf) return 'oneOf';
  return 'string';
}

function resolveRef(schema: JsonSchema, root: JsonSchema): JsonSchema {
  if (!schema.$ref) return schema;
  // schemars refs look like "#/definitions/TypeName"
  const refPath = schema.$ref.replace('#/definitions/', '');
  return root.definitions?.[refPath] ?? schema;
}

function resolveProperties(
  schema: JsonSchema,
  root: JsonSchema,
): Record<string, JsonSchema> | undefined {
  const resolved = resolveRef(schema, root);
  if (resolved.properties) return resolved.properties;
  // schemars sometimes wraps in allOf for flattened types
  if (resolved.allOf) {
    let props: Record<string, JsonSchema> = {};
    for (const sub of resolved.allOf) {
      const r = resolveRef(sub, root);
      if (r.properties) props = { ...props, ...r.properties };
    }
    return Object.keys(props).length > 0 ? props : undefined;
  }
  return undefined;
}
